/**
 * Category recalculation (#3034): PREVIEW, then an explicit APPLY of exactly the displayed parts.
 *
 * The preview is the server's: one page of at most 200 `category_margin` parts of the category tree, each
 * with catalog cost, current and new net price, the margin used, and why a part is left alone. Apply sends the
 * ids of that page and the preview token, nothing else, so no price and no wider target is ever invented by
 * the browser. A stale refusal (409) needs a NEW preview and a fresh look; it is never retried with a new
 * token and never overwritten. After an uncertain outcome the same request and key are held for a retry of
 * the very same payload, and the screen says that a late retry may be refused.
 *
 * Cost-bearing: the whole panel needs `inventory.pricing.manage` AND `reports.view_financial`; without both it
 * renders an explanation and never calls the preview endpoint.
 */

import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { useNotify } from '@/stores/notificationStore';
import {
  useApplyCategoryRecalculation,
  usePreviewCategoryRecalculation,
  usePricingEpoch,
  usePricingPermissions,
  useKeyedSubmit,
  type KeyedSubmitResult,
  type RecalculationApplyBody,
  type RecalculationApplyResult,
  type RecalculationOutcome,
  type RecalculationPreview,
  type RecalculationRow,
} from '@/hooks/usePartPricing';
import { MAX_RECALCULATION_PARTS, formatEuros, formatPercent, formatSignedEuros, impliedMarkupOfMargin } from './decimal';
import { JournalStatusNotice, PricingErrorAlert, UncertainOutcomeAlert } from './feedback';
import { OwnedBoundary } from './OwnedBoundary';
import { isStalePreview } from './pricingErrors';
import { RECALCULATE_PERMISSIONS, isSessionCancel } from './pricingSession';
import { CategoryName, CategoryPicker } from './pickers';

const OUTCOME_COLOR: Record<RecalculationOutcome, 'success' | 'default' | 'warning' | 'error' | 'info'> = {
  change: 'success',
  unchanged: 'default',
  no_rule: 'warning',
  invalid_data: 'error',
  excluded: 'info',
};

const KNOWN_REASONS = new Set([
  'no_margin_rule',
  'outside_category',
  'not_category_margin_mode',
  'part_not_found',
  'invalid_amount',
  'invalid_category_chain',
  'category_cycle',
  'category_depth_exceeded',
  'invalid_margin',
  'invalid_part',
]);

interface DisplayedPreview {
  categoryId: string;
  /** The `afterPartId` of each page up to this one (`null` = the first page). */
  cursors: (string | null)[];
  data: RecalculationPreview;
  /** The pricing epoch the request started at. A higher one now means rules or prices changed since. */
  epoch: number;
  /** The server refused an apply of this preview as stale (409). */
  stale: boolean;
  /** The stale refusal answered a RETRY of an uncertain apply (the first attempt may have gone through). */
  staleAfterRetry: boolean;
  applied: { count: number } | null;
}

function PartCell({ row }: { row: RecalculationRow }) {
  const { t } = useTranslation('partPricing');
  return (
    <>
      <Typography variant="body2">{row.name ?? t('names.unknownPart')}</Typography>
      {row.sku && (
        <Typography variant="caption" color="text.secondary">
          {row.sku}
        </Typography>
      )}
    </>
  );
}

function RecalculationBody() {
  const { t, i18n } = useTranslation('partPricing');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const epoch = usePricingEpoch();
  const epochRef = useRef(epoch);
  epochRef.current = epoch;

  const previewMutation = usePreviewCategoryRecalculation();
  const applyMutation = useApplyCategoryRecalculation();
  // Journaled: an apply whose outcome is unknown is found again, with its exact payload and key, after a remount or a reload.
  const applySubmit = useKeyedSubmit<RecalculationApplyBody, RecalculationApplyResult>(applyMutation.mutateAsync, {
    journal: { kind: 'recalc_apply', target: null, targetOf: (body) => body.categoryId },
    permissions: RECALCULATE_PERMISSIONS,
  });

  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [preview, setPreview] = useState<DisplayedPreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [previewError, setPreviewError] = useState<unknown>(null);
  const [applyError, setApplyError] = useState<unknown>(null);
  /** The token of the preview the user is being asked to confirm; a different preview never inherits the confirmation. */
  const [confirmingToken, setConfirmingToken] = useState<string | null>(null);
  /** The answer to a retried apply that has no displayed preview any more (the screen was remounted or reloaded). */
  const [orphan, setOrphan] = useState<{ kind: 'stale'; afterRetry: boolean } | { kind: 'applied'; count: number } | null>(null);
  /** Only the newest preview request may touch the screen; a slower, older one is dropped. */
  const requestRef = useRef(0);

  const applyBusy = applySubmit.isPending;
  const applyHeld = applySubmit.held !== null;
  const applyDamaged = applySubmit.journalStatus === 'corrupt';
  const locked = applyBusy || applyHeld;

  const loadPreview = async (category: string, cursors: (string | null)[]) => {
    const request = ++requestRef.current;
    const startedAtEpoch = epochRef.current;
    const cursor = cursors[cursors.length - 1] ?? null;
    setLoading(true);
    setPreviewError(null);
    setApplyError(null);
    setOrphan(null);
    setConfirmingToken(null);
    try {
      const data = await previewMutation.mutateAsync({ categoryId: category, ...(cursor ? { afterPartId: cursor } : {}) });
      if (request !== requestRef.current) return;
      setPreview({ categoryId: category, cursors, data, epoch: startedAtEpoch, stale: false, staleAfterRetry: false, applied: null });
    } catch (failure) {
      // An answer of an owner or rights that are gone (or a refusal before it left) is not this screen's to show.
      if (isSessionCancel(failure)) return;
      if (request !== requestRef.current) return;
      setPreview(null);
      setPreviewError(failure);
    } finally {
      if (request === requestRef.current) setLoading(false);
    }
  };

  const changeCategory = (next: string | null) => {
    // Whatever is still on its way belongs to the previous selection.
    requestRef.current += 1;
    setCategoryId(next);
    setPreview(null);
    setPreviewError(null);
    setApplyError(null);
    setOrphan(null);
    setConfirmingToken(null);
    setLoading(false);
  };

  const runApply = async (outcome: Promise<KeyedSubmitResult<RecalculationApplyResult>>, target: DisplayedPreview | null) => {
    const result = await outcome;
    if (result.status === 'success') {
      setApplyError(null);
      setPreview((current) =>
        target && current && current.data.previewToken === target.data.previewToken
          ? { ...current, applied: { count: result.result.appliedCount } }
          : current,
      );
      if (!target) setOrphan({ kind: 'applied', count: result.result.appliedCount });
      notify.success(t('recalc.applied', { count: result.result.appliedCount }));
    } else if (result.status === 'refused') {
      if (isStalePreview(result.error)) {
        if (target) {
          setPreview((current) =>
            current && current.data.previewToken === target.data.previewToken
              ? { ...current, stale: true, staleAfterRetry: result.attempts > 1 }
              : current,
          );
        } else {
          setOrphan({ kind: 'stale', afterRetry: result.attempts > 1 });
        }
        setApplyError(null);
      } else {
        setApplyError(result.error);
      }
    }
    // `uncertain` is shown from the held request below; `busy` / `blocked` / `canceled` change nothing.
  };

  const startApply = (target: DisplayedPreview) => {
    setConfirmingToken(null);
    void runApply(
      applySubmit.submit({
        categoryId: target.categoryId,
        partIds: target.data.rows.map((row) => row.partId),
        previewToken: target.data.previewToken,
      }),
      target,
    );
  };

  const outdated = preview !== null && preview.applied === null && epoch > preview.epoch;
  const rowCount = preview?.data.rows.length ?? 0;
  // A preview being fetched makes the displayed one a thing of the past: it cannot be applied until the new one has arrived and been read.
  const canApply =
    preview !== null &&
    preview.applied === null &&
    !preview.stale &&
    !outdated &&
    !loading &&
    !locked &&
    !applyDamaged &&
    preview.data.summary.change > 0 &&
    rowCount > 0 &&
    rowCount <= MAX_RECALCULATION_PARTS;
  const heldBody = applySubmit.held?.body ?? null;

  const money = (value: string | null) => formatEuros(value, i18n.language);

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="h6">{t('recalc.title')}</Typography>
        <Typography variant="body2" color="text.secondary">
          {t('recalc.intro')}
        </Typography>
      </Box>
      <Alert severity="info">{t('hints.recalcDefinition')}</Alert>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'flex-start' }}>
        <Box sx={{ flex: 1, maxWidth: 480 }}>
          <CategoryPicker label={t('fields.category')} value={categoryId} onChange={changeCategory} disabled={locked} />
        </Box>
        <Button
          variant="contained"
          disabled={!categoryId || loading || locked}
          onClick={() => categoryId && void loadPreview(categoryId, [null])}
        >
          {loading ? tc('loading') : t('recalc.preview')}
        </Button>
      </Stack>

      <PricingErrorAlert error={previewError} />
      <JournalStatusNotice status={applySubmit.journalStatus} holding={applyHeld} onDiscardDamaged={applySubmit.discardDamaged} />
      {heldBody && (
        <Stack spacing={1}>
          <Typography variant="body2">
            {t('recalc.heldCategory')}: <CategoryName id={heldBody.categoryId} /> ({t('recalc.heldParts', { count: heldBody.partIds.length })})
          </Typography>
          <UncertainOutcomeAlert
            what={t('recalc.uncertainWhat')}
            error={applySubmit.error}
            attempts={applySubmit.attempts}
            busy={applyBusy}
            onRetry={() => void runApply(applySubmit.retry(), preview && preview.data.previewToken === heldBody.previewToken ? preview : null)}
            onDiscard={applySubmit.discard}
            journalStatus={applySubmit.journalStatus}
          />
        </Stack>
      )}
      {!preview && orphan?.kind === 'applied' && (
        <Alert severity="success">
          {t('recalc.applied', { count: orphan.count })} {t('recalc.appliedNote')}
        </Alert>
      )}
      {!preview && orphan?.kind === 'stale' && (
        <Alert severity="error">
          <AlertTitle>{t('recalc.staleTitle')}</AlertTitle>
          {orphan.afterRetry ? t('recalc.staleAfterRetry') : t('recalc.staleBody')}
        </Alert>
      )}
      <PricingErrorAlert error={preview ? null : applyError} />

      {preview && (
        <Stack spacing={2}>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip size="small" color="success" label={t('recalc.summary.change', { count: preview.data.summary.change })} />
            <Chip size="small" label={t('recalc.summary.unchanged', { count: preview.data.summary.unchanged })} />
            <Chip size="small" color="warning" label={t('recalc.summary.noRule', { count: preview.data.summary.noRule })} />
            <Chip size="small" color="error" label={t('recalc.summary.invalidData', { count: preview.data.summary.invalidData })} />
            <Chip size="small" color="info" label={t('recalc.summary.excluded', { count: preview.data.summary.excluded })} />
          </Stack>

          {preview.applied && (
            <Alert severity="success">
              {t('recalc.applied', { count: preview.applied.count })} {t('recalc.appliedNote')}
            </Alert>
          )}
          {preview.stale && (
            <Alert
              severity="error"
              action={
                <Button color="inherit" size="small" disabled={loading || locked} onClick={() => void loadPreview(preview.categoryId, preview.cursors)}>
                  {t('recalc.previewAgain')}
                </Button>
              }
            >
              <AlertTitle>{t('recalc.staleTitle')}</AlertTitle>
              {preview.staleAfterRetry ? t('recalc.staleAfterRetry') : t('recalc.staleBody')}
            </Alert>
          )}
          {outdated && !preview.stale && (
            <Alert
              severity="warning"
              action={
                <Button color="inherit" size="small" disabled={loading || locked} onClick={() => void loadPreview(preview.categoryId, preview.cursors)}>
                  {t('recalc.previewAgain')}
                </Button>
              }
            >
              {t('recalc.outdated')}
            </Alert>
          )}
          <PricingErrorAlert error={applyError} />

          {preview.data.hasMore && <Alert severity="info">{t('recalc.moreParts', { count: rowCount })}</Alert>}
          {rowCount === 0 && <Typography color="text.secondary">{t('recalc.noParts')}</Typography>}

          {rowCount > 0 && (
            <TableContainer>
              <Table size="small" aria-label={t('recalc.tableLabel')}>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('recalc.columns.part')}</TableCell>
                    <TableCell>{t('recalc.columns.outcome')}</TableCell>
                    <TableCell align="right">{t('recalc.columns.cost')}</TableCell>
                    <TableCell align="right">{t('recalc.columns.current')}</TableCell>
                    <TableCell align="right">{t('recalc.columns.new')}</TableCell>
                    <TableCell align="right">{t('recalc.columns.change')}</TableCell>
                    <TableCell align="right">{t('recalc.columns.margin')}</TableCell>
                    <TableCell align="right">{t('recalc.columns.markup')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {preview.data.rows.map((row) => (
                    <TableRow key={row.partId} hover>
                      <TableCell>
                        <PartCell row={row} />
                      </TableCell>
                      <TableCell>
                        <Chip size="small" color={OUTCOME_COLOR[row.outcome]} label={t(`recalc.outcome.${row.outcome}`)} />
                        {row.reason && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {KNOWN_REASONS.has(row.reason) ? t(`recalc.reason.${row.reason}`) : t('recalc.reason.unknown')}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">{money(row.costNetCents)}</TableCell>
                      <TableCell align="right">{money(row.currentSellNetCents)}</TableCell>
                      <TableCell align="right">{money(row.newSellNetCents)}</TableCell>
                      <TableCell align="right">{formatSignedEuros(row.changeNetCents, i18n.language)}</TableCell>
                      <TableCell align="right">
                        {row.margin ? (
                          <>
                            {formatPercent(row.margin.percent, i18n.language)}
                            <Typography variant="caption" color="text.secondary" display="block">
                              {t(`recalc.marginSource.${row.margin.source}`)}
                            </Typography>
                          </>
                        ) : null}
                      </TableCell>
                      <TableCell align="right">{row.margin ? (impliedMarkupOfMargin(row.margin.percent, i18n.language) ?? '') : ''}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Button
              size="small"
              disabled={loading || locked || preview.cursors.length <= 1}
              onClick={() => void loadPreview(preview.categoryId, preview.cursors.slice(0, -1))}
            >
              {tc('previous')}
            </Button>
            <Typography variant="caption" color="text.secondary">
              {t('recalc.pageLabel', { page: preview.cursors.length })}
            </Typography>
            <Button
              size="small"
              disabled={loading || locked || !preview.data.hasMore || !preview.data.nextAfterPartId}
              onClick={() => preview.data.nextAfterPartId && void loadPreview(preview.categoryId, [...preview.cursors, preview.data.nextAfterPartId])}
            >
              {tc('next')}
            </Button>
            <Box sx={{ flex: 1 }} />
            <Button variant="contained" color="primary" disabled={!canApply} onClick={() => setConfirmingToken(preview.data.previewToken)}>
              {applyBusy ? tc('loading') : t('recalc.apply', { count: preview.data.summary.change })}
            </Button>
          </Stack>

          <ConfirmDialog
            open={confirmingToken === preview.data.previewToken && canApply}
            title={t('recalc.confirmTitle')}
            message={t('recalc.confirmMessage', { changes: preview.data.summary.change, count: rowCount })}
            confirmText={t('recalc.confirmApply')}
            loading={applyBusy}
            onConfirm={() => startApply(preview)}
            onCancel={() => setConfirmingToken(null)}
          />
        </Stack>
      )}
    </Stack>
  );
}

export function CategoryRecalculation() {
  return (
    <OwnedBoundary>
      <RecalculationGate />
    </OwnedBoundary>
  );
}

/**
 * The cost-bearing screen exists only while `inventory.pricing.manage` AND `reports.view_financial` are held NOW. The boundary
 * above remounts on any change of rights, so a downgrade removes the displayed costs in the same render and an upgrade never
 * brings back a preview taken before.
 */
function RecalculationGate() {
  const { t } = useTranslation('partPricing');
  const { canRecalculate } = usePricingPermissions();
  if (!canRecalculate) {
    return (
      <Alert severity="info">
        <AlertTitle>{t('recalc.title')}</AlertTitle>
        {t('recalc.needsPermission')}
      </Alert>
    );
  }
  return <RecalculationBody />;
}
